/* see docs/EXTRACTED_COMMENTS.md#c224 */

import { enqueueRendererSrc, postUpdateGeometry } from "../worker/RenderWorkerManager.ts";
import {applyDataAndUpdateHidden, setHideFlag, updateParent} from "./ElementUtils.ts";
import type { Component } from "../types.ts";
import {renderInvalidationRegistry, RenderInvalidationReason} from "./RenderInvalidation.ts";
import { ref, type Ref } from "vue";

/* see docs/EXTRACTED_COMMENTS.md#c225 */
export interface DeferredUIOperation {
    type: 'geometry' | 'hidden' | 'parent' | 'src' | 'rule';
    itemId: string;
    item?: Component;
    value?: any;
    contextId?: string;
    execute: () => void;
}

interface DeferredInvalidation {
    reason: RenderInvalidationReason;
    ids: string[];
    shallow?: boolean;
}

/**
 * Update context that tracks a batch of related updates
 */
interface UpdateContext {
    id: string;
    createdAt: number;
    pendingCount: number;
    deferredOperations: DeferredUIOperation[];
    completed: boolean;
    parentContextId: string | null;
    nestedContexts: Set<string>;
    callback: Function;
    hasExecuted: Boolean;
    // Registration window: true while callers are still synchronously registering items
    registrationOpen: boolean;
    invalidations?: DeferredInvalidation[];
}

class UpdateCoordinator {
    private static DEBUG = typeof localStorage !== "undefined"
        && localStorage.getItem('UC_DEBUG') === '1';
    private contexts = new Map<string, UpdateContext>();
    private itemToContext = new Map<string, { contextId: string; count: number }>();
    private contextToItems = new Map<string, Set<string>>();
    private activeContextStack: string[] = [];
    private flushDepth = 0;
    private invalidationBatchingEnabled = true;
    public readonly pendingCount: Ref<number> = ref(0);
    private pendingCountRefreshQueued = false;

    private refreshPendingCount(): void {
        // see docs/EXTRACTED_COMMENTS.md#c226
        // see docs/EXTRACTED_COMMENTS.md#c798
        if (this.pendingCountRefreshQueued) return;
        this.pendingCountRefreshQueued = true;
        queueMicrotask(() => {
            this.pendingCountRefreshQueued = false;
            this.pendingCount.value = this.getPendingCount();
        });
    }

    public setInvalidationBatchingEnabled(enabled: boolean): void {
        this.invalidationBatchingEnabled = enabled;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c227 */
    public createContext(contextId: string, parentContextId: string | null = null, callback?: Function): void {
        if (this.contexts.has(contextId)) {
            // Context already exists — skip duplicate creation
            return;
        }
        // Only log atomic-batch / low-frequency contexts.
        if (
            contextId.includes("datasheet:build") ||
            contextId.includes("initial") ||
            contextId.includes("layout:rebuild") ||
            contextId.includes("reconnect")
        ) {
        }

        // Use provided parent or check the active stack
        const actualParentId = parentContextId || 
            (this.activeContextStack.length > 0 ? this.activeContextStack[this.activeContextStack.length - 1] : null);
        
        const context: UpdateContext = {
            id: contextId,
            createdAt: Date.now(),
            pendingCount: 0,
            deferredOperations: [],
            completed: false,
            parentContextId: actualParentId,
            nestedContexts: new Set(),
            callback: callback || (() => {}),
            hasExecuted: false,
            registrationOpen: true,
            invalidations: [],
        };

        this.contexts.set(contextId, context);
        // see docs/EXTRACTED_COMMENTS.md#c228
        if (actualParentId) {
            const parent = this.contexts.get(actualParentId);
            if (parent) {
                parent.nestedContexts.add(contextId);
            }
        }
        this.activeContextStack.push(contextId);
        this.refreshPendingCount();

    }
    /* see docs/EXTRACTED_COMMENTS.md#c229 */
    public sealRegistration(contextId: string): void {
        const context = this.contexts.get(contextId);
        if (!context) {
            return;
        }
        if (context.completed) {
            return;
        }
        if (!context.registrationOpen) {
            return;
        }
        // Skip the high-volume case.
        if (
            contextId.includes("datasheet:build") ||
            contextId.includes("initial") ||
            contextId.includes("layout:rebuild") ||
            contextId.includes("reconnect")
        ) {
        }
        context.registrationOpen = false;

        // see docs/EXTRACTED_COMMENTS.md#c230
        if (context.pendingCount <= 0) {
            this.tryCompleteContext(contextId);
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c231 */
    public forceCompleteContext(contextId: string): void {
        const context = this.contexts.get(contextId);
        if (!context || context.completed) {
            return;
        }

        // see docs/EXTRACTED_COMMENTS.md#c232
        for (const nestedId of Array.from(context.nestedContexts)) {
            const nested = this.contexts.get(nestedId);
            if (nested && !nested.completed) {
                this.forceCompleteContext(nestedId);
            }
        }


        // see docs/EXTRACTED_COMMENTS.md#c233
        const items = this.contextToItems.get(contextId);
        if (items) {
            for (const itemId of Array.from(items)) {
                const entry = this.itemToContext.get(itemId);
                if (entry && entry.contextId === contextId) {
                    this.itemToContext.delete(itemId);
                }
            }
            items.clear();
        }
        context.pendingCount = 0;
        context.registrationOpen = false;
        this.refreshPendingCount();

        this.tryCompleteContext(contextId);
    }


    /* see docs/EXTRACTED_COMMENTS.md#c234 */
    public registerUpdate(contextId: string, itemId: string): void {
        const context = this.contexts.get(contextId);
        if (!context || context.completed) {
            // see docs/EXTRACTED_COMMENTS.md#c235
            if (
                contextId.includes("datasheet:build") ||
                contextId.includes("initial") ||
                contextId.includes("layout:rebuild") ||
                contextId.includes("reconnect")
            ) {
            }
            return;
        }
        const existing = this.itemToContext.get(itemId);
        if (existing) {
            if (existing.contextId === contextId) {
                // see docs/EXTRACTED_COMMENTS.md#c236
                existing.count++;
                context.pendingCount++;
                this.refreshPendingCount();
                return;
            }
            // see docs/EXTRACTED_COMMENTS.md#c237
            const oldContextId = existing.contextId;
            const oldContext = this.contexts.get(oldContextId);
            if (oldContext && !oldContext.completed) {
                // see docs/EXTRACTED_COMMENTS.md#c238
                let drainBudget = existing.count + 1;
                while (
                    this.itemToContext.get(itemId)?.contextId === oldContextId &&
                    drainBudget-- > 0
                ) {
                    this.completeUpdate(itemId);
                }
                // see docs/EXTRACTED_COMMENTS.md#c239
                if (this.itemToContext.get(itemId)?.contextId === oldContextId) {
                    this.itemToContext.delete(itemId);
                    this.contextToItems.get(oldContextId)?.delete(itemId);
                }
            } else {
                // see docs/EXTRACTED_COMMENTS.md#c240
                this.itemToContext.delete(itemId);
                this.contextToItems.get(oldContextId)?.delete(itemId);
            }
        }
        context.pendingCount++;
        this.itemToContext.set(itemId, { contextId, count: 1 });
        // Only log datasheet/initial registrations to avoid spam from channel.update fan-outs.
        if (contextId.includes("datasheet:build") || contextId.includes("initial")) {
        }
        let itemSet = this.contextToItems.get(contextId);
        if (!itemSet) { itemSet = new Set(); this.contextToItems.set(contextId, itemSet); }
        itemSet.add(itemId);
        this.refreshPendingCount();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c241 */
    public isInContext(itemId: string): boolean {
        const entry = this.itemToContext.get(itemId);
        if (!entry) return false;
        
        const context = this.contexts.get(entry.contextId);
        return context ? !context.completed : false;
    }

    /**
     * Get the context ID for an item, if any
     */
    public getContextId(itemId: string): string | null {
        return this.itemToContext.get(itemId)?.contextId || null;
    }

    /**
     * Check if a context exists
     */
    public getContext(contextId: string): UpdateContext | null {
        return this.contexts.get(contextId) || null;
    }

    /**
     * Defer a UI operation until the context completes
     */
    public deferUIOperation(itemId: string, operation: DeferredUIOperation): void {
        const contextId = this.itemToContext.get(itemId)?.contextId;
        if (!contextId) {
            operation.execute();
            return;
        }

        const context = this.contexts.get(contextId);
        if (!context || context.completed) {
            // see docs/EXTRACTED_COMMENTS.md#c242
            if (contextId.includes("datasheet:build") || contextId.includes("initial") || contextId.includes("layout:rebuild")) {
            }
            operation.execute();
            return;
        }

        operation.contextId = contextId;
        context.deferredOperations.push(operation);
        // Only log atomic-batch ops to reduce channel.update spam.
        if (contextId.includes("datasheet:build") || contextId.includes("initial") || contextId.includes("layout:rebuild")) {
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c243 */
    public getActiveContextId(): string | null {
        for (let i = this.activeContextStack.length - 1; i >= 0; i--) {
            const id = this.activeContextStack[i];
            const ctx = this.contexts.get(id);
            if (ctx && !ctx.completed) return id;
        }
        return null;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c244 */
    public enqueueInvalidation(
        contextId: string,
        reason: RenderInvalidationReason,
        impactedIds: Iterable<string>,
        shallow: boolean,
    ): boolean {
        if (!this.invalidationBatchingEnabled) return false;
        const context = this.contexts.get(contextId);
        if (!context || context.completed) return false;
        const ids = Array.from(impactedIds).filter(Boolean);
        if (ids.length === 0) return true;
        context.invalidations = context.invalidations || [];
        context.invalidations.push({ reason, ids, shallow });
        return true;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c245 */
    public queueInvalidation(itemId: string | null, reason: RenderInvalidationReason, impactedIds: Iterable<string>): void {
        const shallow = reason === RenderInvalidationReason.VisibilityChange;
        if (!this.invalidationBatchingEnabled) {
            renderInvalidationRegistry.requestRender(reason, impactedIds, { shallow, force: true });
            return;
        }
        // see docs/EXTRACTED_COMMENTS.md#c246
        const contextId = itemId ? this.itemToContext.get(itemId)?.contextId : null;
        if (contextId && this.enqueueInvalidation(contextId, reason, impactedIds, shallow)) return;
        renderInvalidationRegistry.requestRender(reason, impactedIds, { shallow, force: true });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c247 */
    public completeUpdate(itemId: string, expectedContextId?: string): void {
        const entry = this.itemToContext.get(itemId);

        if (!entry) return;
        if (expectedContextId && entry.contextId != expectedContextId) return;
        const context = this.contexts.get(entry.contextId);
        if (!context || context.completed) return;
        entry.count--;
        if (entry.count <= 0) {
            this.itemToContext.delete(itemId);
            this.contextToItems.get(entry.contextId)?.delete(itemId);
        }
        context.pendingCount--;
        this.refreshPendingCount();
        if (context.pendingCount <= 0) {
            if (!context.registrationOpen) {
                this.tryCompleteContext(entry.contextId);
            }
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c248 */
    public completeEmptyContext(contextId: string): void {
        const context = this.contexts.get(contextId);
        if (!context || context.completed) return;

        if (UpdateCoordinator.DEBUG) console.log(`[UpdateCoordinator] Empty context ${contextId} - no items to update`);

        // see docs/EXTRACTED_COMMENTS.md#c607
        if (context.registrationOpen) {
            return;
        }


        // see docs/EXTRACTED_COMMENTS.md#c608
        if (context.pendingCount <= 0) {
            this.tryCompleteContext(contextId);
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c609 */
    private tryCompleteContext(contextId: string): void {
        // see docs/EXTRACTED_COMMENTS.md#c610
        let currentId: string | null = contextId;
        while (currentId) {
            const context = this.contexts.get(currentId);
            if (!context || context.completed) {
                return;
            }

            // see docs/EXTRACTED_COMMENTS.md#c611
            if (context.registrationOpen) {
                return;
            }

            if (UpdateCoordinator.DEBUG) console.log(`[UpdateCoordinator] Trying to complete context ${currentId} (pending: ${context.pendingCount}, nested: ${context.nestedContexts.size})`);
            if (context.pendingCount > 0) {
                return;
            }

            // Check if all nested contexts are completed
            const incompleteNested: string[] = [];
            for (const nestedId of context.nestedContexts) {
                const nestedContext = this.contexts.get(nestedId);
                if (nestedContext && !nestedContext.completed) {
                    incompleteNested.push(nestedId);
                }
            }

            if (incompleteNested.length > 0) {
                if (UpdateCoordinator.DEBUG) console.log(`[UpdateCoordinator] Context ${currentId} waiting for ${incompleteNested.length} nested contexts: ${incompleteNested.join(', ')}`);
                return;
            }

            // see docs/EXTRACTED_COMMENTS.md#c612
            context.completed = true;
            // Skip per-completion log for high-volume contexts.
            if (
                currentId.includes("datasheet:build") ||
                currentId.includes("initial") ||
                currentId.includes("layout:rebuild") ||
                currentId.includes("reconnect") ||
                context.deferredOperations.length > 5 ||
                (context.invalidations?.length ?? 0) > 5
            ) {
            }
            if (UpdateCoordinator.DEBUG) console.log(`[UpdateCoordinator] Context ${currentId} ready to complete (all nested done)`);

            // Pop from active stack
            const stackIndex = this.activeContextStack.indexOf(currentId);
            if (stackIndex !== -1) {
                this.activeContextStack.splice(stackIndex, 1);
            }

            const parent = context.parentContextId
                ? this.contexts.get(context.parentContextId)
                : undefined;
            if (parent) {
                // see docs/EXTRACTED_COMMENTS.md#c613
                for (const op of context.deferredOperations) {
                    parent.deferredOperations.push(op);
                }
                if (context.invalidations?.length) {
                    parent.invalidations = parent.invalidations || [];
                    for (const inv of context.invalidations) {
                        parent.invalidations.push(inv);
                    }
                }
                this.evictContext(context);
                // see docs/EXTRACTED_COMMENTS.md#c614
                if (parent.pendingCount <= 0) {
                    currentId = parent.id;
                    continue;
                }
                return;
            }
            // see docs/EXTRACTED_COMMENTS.md#c615

            // Root context: flush entire tree
            const cb = context.callback;
            // Ensure callback is exactly-once even if completion is re-entered.
            context.callback = () => {};
            this.flushContextTree(currentId);
            try {
                cb?.();
            } catch (e) {
                console.error("[UpdateCoordinator] Root callback error", e);
            }
            return;
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c616 */
    private evictContext(context: UpdateContext): void {
        const items = this.contextToItems.get(context.id);
        if (items) {
            for (const itemId of items) {
                const entry = this.itemToContext.get(itemId);
                if (entry && entry.contextId === context.id) {
                    this.itemToContext.delete(itemId);
                }
            }
            this.contextToItems.delete(context.id);
        }
        const parent = context.parentContextId ? this.contexts.get(context.parentContextId) : undefined;
        parent?.nestedContexts.delete(context.id);
        this.contexts.delete(context.id);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c617 */
    private flushContextTree(contextId: string): void {
        const context = this.contexts.get(contextId);
        if (!context) {
            return;
        }
        this.flushDepth++;

        const allOperations = context.deferredOperations;
        const invalidations = context.invalidations ?? [];

        if (UpdateCoordinator.DEBUG) console.log(`[UpdateCoordinator] Flushing root context ${contextId}: ${allOperations.length} deferred operations`);

        try {
            for (const operation of allOperations) {
                try {
                    operation.execute();
                } catch (error) {
                    console.error(`[UpdateCoordinator] Error executing deferred operation for ${operation.itemId}:`, error);
                }
            }

            // Dispatch invalidations in a single deduped batch
            if (invalidations.length > 0) {
                const byReason = new Map<RenderInvalidationReason, { ids: Set<string>; shallow: boolean }>();
                for (const inv of invalidations) {
                    const bucket = byReason.get(inv.reason) || { ids: new Set<string>(), shallow: inv.shallow === true };
                    for (const id of inv.ids) {
                        if (id) bucket.ids.add(id);
                    }
                    // If any entry says shallow=false, ensure bucket shallow=false
                    bucket.shallow = bucket.shallow && inv.shallow === true;
                    byReason.set(inv.reason, bucket);
                }
                const entries = Array.from(byReason.entries()).map(([reason, bucket]) => {
                    return { reason, ids: Array.from(bucket.ids), shallow: bucket.shallow };
                });
                renderInvalidationRegistry.requestRenderBatch(entries);
            }

            this.evictContext(context);
            this.refreshPendingCount();
        } finally {
            this.flushDepth = Math.max(0, this.flushDepth - 1);
        }
    }

    /**
     * Helper: Defer a geometry update
     */
    public deferGeometryUpdate(itemId: string, item: Component): void {
        this.deferUIOperation(itemId, {
            type: 'geometry',
            itemId,
            item,
            execute: () => {
                postUpdateGeometry(item.props.id, "context-flush");
            }
        });
    }

    /**
     * Helper: Defer a hidden update
     */
    public deferHiddenUpdate(itemId: string, _item: Component, data: any): void {
        this.deferUIOperation(itemId, {
            type: 'hidden',
            itemId,
            execute: () => applyDataAndUpdateHidden(itemId, data)
        });
    }

    /**
     * Helper: Defer a parent update
     */
    public deferParentUpdate(itemId: string): void {
        this.deferUIOperation(itemId, {
            type: 'parent',
            itemId,
            execute: () => updateParent(itemId)
        });
    }

    /**
     * Helper: Defer a src update
     */
    public deferSrcUpdate(itemId: string, item: Component, src: string): void {
        this.deferUIOperation(itemId, {
            type: 'src',
            itemId,
            item,
            value: src,
            execute: () => {
                // Only a new src clears a failed image: an unchanged src would just unhide the broken image
                // (DataObjectImage owns the retry ladder for the same src).
                if (enqueueRendererSrc(item, src)) {
                    setHideFlag(itemId, "image", false, "defer_src_update");
                }
            }
        });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c618 */
    public deferRuleCallback(itemId: string, callback: () => void): void {
        this.deferUIOperation(itemId, {
            type: 'rule',
            itemId,
            execute: callback
        });
    }



    /* see docs/EXTRACTED_COMMENTS.md#c619 */
    public removeFromActiveStack(contextId: string): void {
        const idx = this.activeContextStack.indexOf(contextId);
        if (idx !== -1) this.activeContextStack.splice(idx, 1);
        // see docs/EXTRACTED_COMMENTS.md#c696
        const ctx = this.contexts.get(contextId);
        if (ctx?.parentContextId) {
            this.contexts.get(ctx.parentContextId)?.nestedContexts.delete(contextId);
            ctx.parentContextId = null;
        }
    }

    /**
     * Get debug info about active contexts
     */
    public getDebugInfo(): string {
        const activeContexts = Array.from(this.contexts.values()).filter(c => !c.completed);
        const rootContexts = activeContexts.filter(c => !c.parentContextId);
        const now = Date.now();
        let info = `Active contexts: ${activeContexts.length} (${rootContexts.length} root), `;
        info += `Item mappings: ${this.itemToContext.size}, `;
        info += `Stack depth: ${this.activeContextStack.length}`;
        
        if (activeContexts.length > 0) {
            info += `\n  Contexts: ${activeContexts.map(c => 
                `${c.id}(pending: ${c.pendingCount}, nested: ${c.nestedContexts.size}, ageMs: ${Math.max(0, now - c.createdAt)})`
            ).join(', ')}`;
        }
        
        return info;
    }

    /**
     * Returns active contexts older than a threshold for watchdog telemetry.
     */
    public getLongLivedContexts(thresholdMs: number = 5000): Array<{
        id: string;
        ageMs: number;
        pendingCount: number;
        nestedCount: number;
        parentContextId: string | null;
        registrationOpen: boolean;
        deferredOperations: number;
    }> {
        const now = Date.now();
        const stalled: Array<{
            id: string;
            ageMs: number;
            pendingCount: number;
            nestedCount: number;
            parentContextId: string | null;
            registrationOpen: boolean;
            deferredOperations: number;
        }> = [];

        for (const context of this.contexts.values()) {
            if (context.completed) continue;
            const ageMs = Math.max(0, now - context.createdAt);
            if (ageMs < thresholdMs) continue;
            stalled.push({
                id: context.id,
                ageMs,
                pendingCount: context.pendingCount,
                nestedCount: context.nestedContexts.size,
                parentContextId: context.parentContextId,
                registrationOpen: context.registrationOpen,
                deferredOperations: context.deferredOperations.length,
            });
        }

        stalled.sort((a, b) => b.ageMs - a.ageMs);
        return stalled;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c249 */
    public dumpStuckContexts(): Array<{
        id: string;
        ageMs: number;
        pendingCount: number;
        registrationOpen: boolean;
        completed: boolean;
        parentContextId: string | null;
        nestedContexts: string[];
        items: Array<{ id: string; count: number }>;
        deferredOperationsCount: number;
        deferredOperationTypes: string[];
        hasExecuted: boolean;
    }> {
        const now = Date.now();
        const rows: Array<{
            id: string;
            ageMs: number;
            pendingCount: number;
            registrationOpen: boolean;
            completed: boolean;
            parentContextId: string | null;
            nestedContexts: string[];
            items: Array<{ id: string; count: number }>;
            deferredOperationsCount: number;
            deferredOperationTypes: string[];
            hasExecuted: boolean;
        }> = [];
        for (const context of this.contexts.values()) {
            const itemIds = this.contextToItems.get(context.id) ?? new Set<string>();
            const items: Array<{ id: string; count: number }> = [];
            for (const itemId of itemIds) {
                const entry = this.itemToContext.get(itemId);
                items.push({
                    id: itemId,
                    count: entry?.contextId === context.id ? entry.count : 0,
                });
            }
            rows.push({
                id: context.id,
                ageMs: Math.max(0, now - context.createdAt),
                pendingCount: context.pendingCount,
                registrationOpen: context.registrationOpen,
                completed: context.completed,
                parentContextId: context.parentContextId,
                nestedContexts: Array.from(context.nestedContexts),
                items,
                deferredOperationsCount: context.deferredOperations.length,
                deferredOperationTypes: context.deferredOperations.map((op) => op.type),
                hasExecuted: !!context.hasExecuted,
            });
        }
        rows.sort((a, b) => b.ageMs - a.ageMs);
        return rows;
    }

    /**
     * Get total pending updates across active contexts.
     */
    public getPendingCount(): number {
        let total = 0;
        for (const context of this.contexts.values()) {
            if (context.completed) continue;
            total += context.pendingCount;
        }
        return total;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c250 */
    public hasActiveContexts(): boolean {
        if (this.flushDepth > 0) return true;
        for (const context of this.contexts.values()) {
            if (!context.completed) return true;
        }
        return false;
    }

    /**
     * Clears the current state.
     */
    public clear(): void {
        this.activeContextStack.splice(0, this.activeContextStack.length);
        this.itemToContext.clear();
        this.contextToItems.clear();
        this.contexts.clear();
        this.refreshPendingCount();
    }

    /**
     * Whether the given context exists and has not yet completed.
     */
    public isContextActive(contextId: string): boolean {
        const ctx = this.contexts.get(contextId);
        return ctx != null && !ctx.completed;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c251 */
    public checkValidity(contextId: string): void {
        const context = this.contexts.get(contextId);
        if (!context || context.completed) return;
        if (context.pendingCount <= 0) {
            this.tryCompleteContext(contextId);
        }
    }
}

// Singleton instance
const coordinator = new UpdateCoordinator();

export default coordinator;

